import type { User, ApiKey, Invitation } from '../types';

const AUTH_API_BASE = import.meta.env.VITE_AUTH_API_URL || 'http://localhost:8787';

class AuthAPIClient {
  private isRedirecting = false;

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    // Prevent making requests if we're already redirecting
    if (this.isRedirecting) {
      throw new Error('Redirecting to login');
    }

    const response = await fetch(`${AUTH_API_BASE}${endpoint}`, {
      ...options,
      credentials: 'include', // Include session cookies
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (response.status === 401) {
      // Set flag to prevent further requests
      this.isRedirecting = true;

      // Clear any stored auth state
      localStorage.removeItem('varinym-auth');

      // Use a custom error that React Query won't retry
      const error = new Error('Unauthorized') as Error & { shouldRedirect: boolean };
      error.shouldRedirect = true;
      throw error;
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'API request failed');
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return undefined as T;
    }

    // Handle responses with no content
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      return undefined as T;
    }

    return response.json();
  }

  // Authentication
  login() {
    window.location.href = `${AUTH_API_BASE}/auth/login`;
  }

  logout() {
    // Clear stored auth state before redirecting
    localStorage.removeItem('varinym-auth');
    window.location.href = `${AUTH_API_BASE}/auth/logout`;
  }

  async getMe(): Promise<User> {
    return this.request('/me');
  }

  async checkProfileComplete(): Promise<{
    user: User;
    needsProfileCompletion: boolean;
    currentCompanyName: string;
  }> {
    return this.request('/profile/complete');
  }

  async completeProfile(companyName: string): Promise<void> {
    return this.request('/profile/update', {
      method: 'POST',
      body: JSON.stringify({ company: companyName }),
    });
  }

  // User Management
  async listUsers(): Promise<User[]> {
    return this.request('/api/users');
  }

  async updateUserRole(userId: string, role: 'admin' | 'user'): Promise<void> {
    return this.request(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  async deleteUser(userId: string): Promise<void> {
    return this.request(`/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // Invitation Management
  async listInvitations(): Promise<Invitation[]> {
    return this.request('/invitations');
  }

  async createInvitation(email: string, role: 'admin' | 'user'): Promise<void> {
    return this.request('/invitations', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
  }

  async resendInvitation(invitationId: string): Promise<void> {
    // Note: Resend endpoint not documented in AUTH_INTEGRATION.md
    // Keeping for backwards compatibility, but may need to be removed
    return this.request(`/invitations/${invitationId}/resend`, {
      method: 'POST',
    });
  }

  async deleteInvitation(invitationId: string): Promise<void> {
    return this.request(`/invitations/${invitationId}`, {
      method: 'DELETE',
    });
  }

  // API Key Management
  async listApiKeys(): Promise<ApiKey[]> {
    return this.request('/api/api-keys');
  }

  async createApiKey(name: string): Promise<{ id: string; key: string }> {
    return this.request('/api-keys', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async deleteApiKey(keyId: string): Promise<void> {
    return this.request(`/api-keys/${keyId}`, {
      method: 'DELETE',
    });
  }
}

export const authAPI = new AuthAPIClient();
